import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { ecommerceService as svc } from '@/services/ecommerce.service';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { StoreLandingView } from '@/components/ecommerce/StoreLandingView';
import { OwnerEventsSection } from '@/components/public/OwnerEventsSection';
import { CetsTrees } from '@/components/CetsTrees';
import { StorefrontBanner } from '@/components/StorefrontBanner';
import { PackageX, EyeOff } from 'lucide-react';

export default function ShopStore() {
  const { ownerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null); setError('');
    (async () => {
      try { const d = await svc.publicShop(ownerId); if (alive) setData(d); }
      catch (e) { if (alive) setError('Negozio non trovato o non disponibile.'); }
    })();
    return () => { alive = false; };
  }, [ownerId]);

  return (
    <PublicLayout>
      <StorefrontBanner ownerId={ownerId} />
      <Seo
        title={data ? `${data.name} — Shop Sila` : 'Negozio — Shop Sila'}
        description={data?.tagline || 'Vetrina prodotti tipici della Sila'}
        image={data?.hero_image ? mediaUrl(data.hero_image) : undefined}
      />
      <section className="sila-section py-8" data-testid="store-page">
        {error ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="store-error">
            <PackageX className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 font-medium text-foreground">{error}</p>
            <Button asChild variant="outline" className="mt-4"><Link to="/shop">Torna allo Shop</Link></Button>
          </div>
        ) : !data ? (
          <div className="space-y-6">
            <Skeleton className="aspect-[21/9] max-h-[420px] w-full rounded-2xl" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-64 w-full rounded-xl" />)}
            </div>
          </div>
        ) : (
          <>
            {data.published === false && (
              <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300" data-testid="store-unpublished">
                <EyeOff className="h-4 w-4" />
                Questa vetrina non è ancora pubblicata: solo tu puoi vederla in anteprima.
              </div>
            )}
            <StoreLandingView data={data} ownerId={ownerId} interactive />
            <div className="mt-5"><CetsTrees ownerId={ownerId} size="md" data-testid="cets-vetrina-badge" /></div>
            <OwnerEventsSection ownerId={ownerId} />
          </>
        )}
      </section>
    </PublicLayout>
  );
}
